import * as React from 'react'
import { useForm, UseFormGetValues, UseFormSetValue } from 'react-hook-form'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { Swiper } from 'swiper/types'

import {
  SearchCondition as SearchConditionPresenter,
  IProps as ISearchConditionPresenterProps,
} from './presenter'
export { makeSearchConditionSummaryText } from './presenter'
import {
  trackingUserContext,
  searchEventIdContext,
} from '@/contexts/trackingContexts'
import { isMobileDisplay } from '@/utils/devise'
import { trackEvent, ITrackEventItem } from '@/utils/analyze'
import { dateToStringWithFormat } from '@/utils/dateFormat'

interface IProps
  extends Omit<
    ISearchConditionPresenterProps,
    | 'setSwiper'
    | 'setSubmitSwiper'
    | 'register'
    | 'setValue'
    | 'currentPrefectureId'
    | 'setPrefectureId'
    | 'areas'
    | 'stations'
    | 'selectedPrefectures'
    | 'selectedGeocodes'
    | 'selectedStations'
    | 'selectedSearchType'
    | 'selectedReserveType'
    | 'changeSearchTypeHandler'
    | 'openPrefectureHandler'
    | 'closePrefectureHandler'
    | 'resetPrefectureHandler'
    | 'openAreaSelectHandler'
    | 'closeAreaSelectHandler'
    | 'transitionEndHandler'
    | 'selectPrefectureHandler'
    | 'selectAllGeocodeHandler'
    | 'deselectAllGeocodeHandler'
    | 'selectGeocodeHandler'
    | 'selectStationHandler'
    | 'prevHandler'
    | 'submitHandler'
    | 'resetHandler'
    | 'submitSearchTopHandler'
    | 'openDateTimeHandler'
    | 'closeDateTimeHandler'
    | 'submitDateTimeHandler'
    | 'submitDateTimeRegularHandler'
    | 'searchState'
  > {
  showSearchCount?: boolean
  getValues: UseFormGetValues<ISearchConditionState>
  submitHandler: (state: ISearchConditionState) => void
}

const TRANSITION_SPEED = 0
const MOBIL_TRANSITION_SPEED = 300
const DEFAULT_STATE_RESERVE_TYPE = 'normal' as const
const DEFAULT_STATE_SEARCH = {
  search_type: 'sitter' as const,
  geocode: {},
  station: {},
  available_schedule: 1,
  nursery_age0_0: 0,
  nursery_age0_1to2: 0,
  nursery_age0_3to5: 0,
  nursery_age0_6to11: 0,
  nursery_age1: 0,
  nursery_age2: 0,
  nursery_age3to6: 0,
  nursery_age7to12: 0,
  acceptable_number1: 0,
  acceptable_number2: 0,
  cao_bs: 0,
  tokyo_bs_tmp_use: 0,
  tokyo_bs_waiting: 0,
  regular_discount: 0,
  campaign: 0,
  housekeeping_subsidy: 0,
  hourly_wage_from_1000_to_1500: 0,
  hourly_wage_from_1500_to_2000: 0,
  hourly_wage_from_2000_to_2500: 0,
  hourly_wage_from_2500_to_3000: 0,
  hourly_wage_from_3000: 0,
  housekeeping_clean_up_support: 0,
  housekeeping_tidy_up_support: 0,
  housekeeping_home_cooking_support: 0,
  housekeeping_shopping: 0,
  housekeeping_cooking_in_advance_support: 0,
  housekeeping_home_party_cooking: 0,
  housekeeping_clean_with_cooking: 0,
  housekeeping_cooking_for_food_industry: 0,
  tutor_kindergarten: 0,
  tutor_elementary: 0,
  tutor_jrhighschool: 0,
  tutor_highschool: 0,
  tutor_tutor_supplementary: 0,
  tutor_english_bilingual: 0,
  tutor_entrance_exam: 0,
  tutor_tokyo_university: 0,
  tutor_keio_university: 0,
  tutor_waseda_university: 0,
  tutor_sophia_university: 0,
  tutor_other_university: 0,
  tutor_elementary_exam: 0,
  tutor_jrhighschool_exam: 0,
  tutor_highschool_exam: 0,
  tutor_college_exam: 0,
  tutor_elementary_exam_ex: 0,
  tutor_jrhighschool_exam_ex: 0,
  tutor_highschool_exam_ex: 0,
  tutor_college_exam_ex: 0,
  tutor_national_language: 0,
  tutor_arithmetic: 0,
  tutor_science: 0,
  tutor_social_studies: 0,
  tutor_english: 0,
  tutor_english_speaking: 0,
  tutor_toeic: 0,
  tutor_toefl: 0,
  tutor_english_exam: 0,
  tutor_math: 0,
  tutor_ancient_writing: 0,
  tutor_chinese_classics: 0,
  tutor_essay: 0,
  tutor_physics: 0,
  tutor_chemistry: 0,
  tutor_biology: 0,
  tutor_geoscience: 0,
  tutor_japanese_history: 0,
  tutor_world_history: 0,
  tutor_geography: 0,
  tutor_ethics: 0,
  tutor_politics_economics: 0,
  cred: {
    childminder: 0,
    kindergarten_teacher: 0,
    nurse: 0,
  },
  doula_certification: 0,
  doula_certification_official: 0,
  nursing_doula: 0,
  doulaedu_certification: 0,
  pickup_only_support: 0,
  mid_night_support: 0,
  handicap_support: 0,
  tutor_enable_flag: 0,
  english_lesson_support: 0,
  music_lesson_support: 0,
  sports_lesson_support: 0,
  art_lesson_support: 0,
  other_appeal_support: 0,
  morning_support: 0,
  night_support: 0,
  sick_bsl_fever: 0,
  sick_bsl_unknown_disease: 0,
  sick_bsl_infection: 0,
  sick_bsl_unknown: 0,
  illness_bsl_fever: 0,
  illness_bsl_unknown_disease: 0,
  illness_bsl_infection: 0,
  new_debuted_sitter: 0,
  new_debuted_housekeeper: 0,
  foreign_language_support: 0,
  semi_childcare_certification: 0,
  no_option: 0,
  word: '',
}
const DEFAULT_STATE_REGULAR_SEARCH = {
  monday: undefined,
  tuesday: undefined,
  wednesday: undefined,
  thursday: undefined,
  friday: undefined,
  saturday: undefined,
  sunday: undefined,
}

export const initConditionStateWithSearchType = (
  selectedSerchType: ISearchType,
  getValues: UseFormGetValues<ISearchConditionState>,
  setValue: UseFormSetValue<ISearchConditionState>,
) => {
  const {
    search_type,
    available_schedule,
    geocode,
    station,
    nursery_age0_0,
    nursery_age0_1to2,
    nursery_age0_3to5,
    nursery_age0_6to11,
    nursery_age1,
    nursery_age2,
    nursery_age3to6,
    nursery_age7to12,
    acceptable_number1,
    acceptable_number2,
    ...search
  } = getValues('search')
  const regularSearch = getValues('regular_search')

  // 種別に変更がないときは何もしない
  if (search_type === selectedSerchType) {
    return
  }
  // ベビーシッター、産前産後の場合は保育可能年齢・人数を維持する
  if (selectedSerchType === 'sitter' || selectedSerchType === 'doula') {
    setValue('search', {
      ...search,
      ...DEFAULT_STATE_SEARCH,
      available_schedule,
      search_type: selectedSerchType,
      geocode,
      station,
      nursery_age0_0,
      nursery_age0_1to2,
      nursery_age0_3to5,
      nursery_age0_6to11,
      nursery_age1,
      nursery_age2,
      nursery_age3to6,
      nursery_age7to12,
      acceptable_number1,
      acceptable_number2,
    })
  } else {
    setValue('search', {
      ...search,
      ...DEFAULT_STATE_SEARCH,
      available_schedule,
      search_type: selectedSerchType,
      geocode,
      station,
    })
  }
  setValue('regular_search', {
    ...regularSearch,
    ...DEFAULT_STATE_REGULAR_SEARCH,
    sunday: regularSearch?.sunday,
    monday: regularSearch?.monday,
    tuesday: regularSearch?.tuesday,
    wednesday: regularSearch?.wednesday,
    thursday: regularSearch?.thursday,
    friday: regularSearch?.friday,
    saturday: regularSearch?.saturday,
  })
}

const makeTrackItem = (itemId: string, itemName: string): ITrackEventItem => ({
  itemId,
  itemName,
})

export const makeSearchConditionTrackingData = (
  state: ISearchConditionState,
  t: TFunction,
): ITrackEventItem[] => {
  const items: ITrackEventItem[] = []

  // 予約可能なサポーター
  if (state?.search?.available_schedule) {
    items.push(
      makeTrackItem(
        'search_available_schedule',
        t('search:params.search.availableSchedule') as string,
      ),
    )
  }

  // 市区町村
  if (state?.search?.geocode) {
    // 一次元配列に変換
    const geocodes: IGeocode[] = ([] as IGeocode[]).concat(
      ...Object.values(state.search.geocode),
    )
    if (geocodes.length > 0) {
      geocodes.forEach((geocode) => {
        items.push(
          makeTrackItem(
            `search_geocode_${geocode.geocode_id}`,
            `市区町村: ${geocode.name}`,
          ),
        )
      })
    }
  }

  // 主要駅
  if (state?.search?.station) {
    // 一次元配列に変換
    const stations: IStation[] = ([] as IStation[]).concat(
      ...Object.values(state.search.station),
    )

    if (stations.length > 0) {
      stations.forEach((station) => {
        items.push(
          makeTrackItem(
            `search_station_${station.slug}`,
            `主要駅: ${station.name}`,
          ),
        )
      })
    }
  }

  // 予約方法
  if (state.reserveType) {
    const reserveType = t(
      `search:params.search.reserveType.${state.reserveType}`,
    ) as string

    items.push(
      makeTrackItem(
        `search_reserve_type_${reserveType}`,
        `予約方法: ${reserveType}`,
      ),
    )
  }

  // 曜日指定
  if (state?.regular_search?.sunday) {
    items.push(
      makeTrackItem(
        `search_regular_search_sunday`,
        `曜日指定: ${t('search:params.regularSearch.sunday') as string}`,
      ),
    )
  }
  if (state?.regular_search?.monday) {
    items.push(
      makeTrackItem(
        `search_regular_search_monday`,
        `曜日指定: ${t('search:params.regularSearch.monday') as string}`,
      ),
    )
  }
  if (state?.regular_search?.tuesday) {
    items.push(
      makeTrackItem(
        `search_regular_search_tuesday`,
        `曜日指定: ${t('search:params.regularSearch.tuesday') as string}`,
      ),
    )
  }
  if (state?.regular_search?.wednesday) {
    items.push(
      makeTrackItem(
        `search_regular_search_wednesday`,
        `曜日指定: ${t('search:params.regularSearch.wednesday') as string}`,
      ),
    )
  }
  if (state?.regular_search?.thursday) {
    items.push(
      makeTrackItem(
        `search_regular_search_thursday`,
        `曜日指定: ${t('search:params.regularSearch.thursday') as string}`,
      ),
    )
  }
  if (state?.regular_search?.friday) {
    items.push(
      makeTrackItem(
        `search_regular_search_friday`,
        `曜日指定: ${t('search:params.regularSearch.friday') as string}`,
      ),
    )
  }
  if (state?.regular_search?.saturday) {
    items.push(
      makeTrackItem(
        `search_regular_search_saturday`,
        `曜日指定: ${t('search:params.regularSearch.saturday') as string}`,
      ),
    )
  }

  // 指導対象
  if (state?.search?.tutor_kindergarten) {
    items.push(
      makeTrackItem(
        `search_tutor_kindergarten`,
        `指導対象: ${t('search:params.search.tutorKindergarten') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_elementary) {
    items.push(
      makeTrackItem(
        `search_tutor_elementary`,
        `指導対象: ${t('search:params.search.tutorElementary') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_jrhighschool) {
    items.push(
      makeTrackItem(
        `search_tutor_jrhighschool`,
        `指導対象: ${t('search:params.search.tutorJrhighschool') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_highschool) {
    items.push(
      makeTrackItem(
        `search_tutor_highschool`,
        `指導対象: ${t('search:params.search.tutorHighschool') as string}`,
      ),
    )
  }

  // 保育可能年齢
  if (state?.search?.nursery_age0_0) {
    items.push(
      makeTrackItem(
        'search_nursery_age_0-0',
        `保育可能年齢: ${t('search:params.search.nurseryAge0-0') as string}`,
      ),
    )
  }
  if (state?.search?.nursery_age0_1to2) {
    items.push(
      makeTrackItem(
        'search_nursery_age_0-1_0-2',
        `保育可能年齢: ${t('search:params.search.nurseryAge0-1to2') as string}`,
      ),
    )
  }
  if (state?.search?.nursery_age0_3to5) {
    items.push(
      makeTrackItem(
        'search_nursery_age_0-3_0-5',
        `保育可能年齢: ${t('search:params.search.nurseryAge0-3to5') as string}`,
      ),
    )
  }
  if (state?.search?.nursery_age0_6to11) {
    items.push(
      makeTrackItem(
        'search_nursery_age_0-6_0-11',
        `保育可能年齢: ${
          t('search:params.search.nurseryAge0-6to11') as string
        }`,
      ),
    )
  }
  if (state?.search?.nursery_age1) {
    items.push(
      makeTrackItem(
        'search_nursery_age_1',
        `保育可能年齢: ${t('search:params.search.nurseryAge1') as string}`,
      ),
    )
  }
  if (state?.search?.nursery_age2) {
    items.push(
      makeTrackItem(
        'search_nursery_age_2',
        `保育可能年齢: ${t('search:params.search.nurseryAge2') as string}`,
      ),
    )
  }
  if (state?.search?.nursery_age3to6) {
    items.push(
      makeTrackItem(
        'search_nursery_age_3_6',
        `保育可能年齢: ${t('search:params.search.nurseryAge3to6') as string}`,
      ),
    )
  }
  if (state?.search?.nursery_age7to12) {
    items.push(
      makeTrackItem(
        'search_nursery_age_7_12',
        `保育可能年齢: ${t('search:params.search.nurseryAge7to12') as string}`,
      ),
    )
  }

  // 保育人数
  if (state?.search?.acceptable_number1) {
    items.push(
      makeTrackItem(
        'search_acceptable_number_1',
        `保育人数: ${t('search:params.search.acceptableNumber1') as string}`,
      ),
    )
  }
  if (state?.search?.acceptable_number2) {
    items.push(
      makeTrackItem(
        'search_acceptable_number_2',
        `保育人数: ${t('search:params.search.acceptableNumber2') as string}`,
      ),
    )
  }

  // 割引対象
  if (state?.search?.cao_bs) {
    items.push(
      makeTrackItem(
        'search_cao_bs',
        `割引対象: ${t('search:params.search.caoBs') as string}`,
      ),
    )
  }
  if (state?.search?.tokyo_bs_tmp_use) {
    items.push(
      makeTrackItem(
        'search_tokyo_bs_tmp_use',
        `割引対象: ${t('search:params.search.tokyoBsTmpUse') as string}`,
      ),
    )
  }
  if (state?.search?.tokyo_bs_waiting) {
    items.push(
      makeTrackItem(
        'search_tokyo_bs_waiting',
        `割引対象: ${t('search:params.search.tokyoBsWaiting') as string}`,
      ),
    )
  }
  if (state?.search?.regular_discount) {
    items.push(
      makeTrackItem(
        'search_regular_discount',
        `割引対象: ${t('search:params.search.regularDiscount') as string}`,
      ),
    )
  }
  if (state?.search?.campaign) {
    items.push(
      makeTrackItem(
        'search_campaign',
        `割引対象: ${t('search:params.search.campaign') as string}`,
      ),
    )
  }
  if (state?.search?.housekeeping_subsidy) {
    items.push(
      makeTrackItem(
        'search_housekeeping_subsidy',
        `割引対象: ${t('search:params.search.housekeeping_subsidy') as string}`,
      ),
    )
  }

  // 時給
  if (state?.search?.hourly_wage_from_1000_to_1500) {
    items.push(
      makeTrackItem(
        'search_hourly_wage_from_1000_to_1500',
        `時給: ${t('search:params.search.hourlyWageFrom1000To1500') as string}`,
      ),
    )
  }
  if (state?.search?.hourly_wage_from_1500_to_2000) {
    items.push(
      makeTrackItem(
        'search_hourly_wage_from_1500_to_2000',
        `時給: ${t('search:params.search.hourlyWageFrom1500To2000') as string}`,
      ),
    )
  }
  if (state?.search?.hourly_wage_from_2000_to_2500) {
    items.push(
      makeTrackItem(
        'search_hourly_wage_from_2000_to_2500',
        `時給: ${t('search:params.search.hourlyWageFrom2000To2500') as string}`,
      ),
    )
  }
  if (state?.search?.hourly_wage_from_2500_to_3000) {
    items.push(
      makeTrackItem(
        'search_hourly_wage_from_2500_to_3000',
        `時給: ${t('search:params.search.hourlyWageFrom2500To3000') as string}`,
      ),
    )
  }
  if (state?.search?.hourly_wage_from_3000) {
    items.push(
      makeTrackItem(
        'search_hourly_wage_from_3000',
        `時給: ${t('search:params.search.hourlyWageFrom3000') as string}`,
      ),
    )
  }

  // 資格
  if (state?.search?.cred?.childminder) {
    items.push(
      makeTrackItem(
        'search_cred_childminder',
        `資格: ${t('search:params.search.cred.childminder') as string}`,
      ),
    )
  }
  if (state?.search?.cred?.kindergarten_teacher) {
    items.push(
      makeTrackItem(
        'search_cred_kindergarten_teacher',
        `資格: ${t('search:params.search.cred.kindergartenTeacher') as string}`,
      ),
    )
  }
  if (state?.search?.cred?.nurse) {
    items.push(
      makeTrackItem(
        'search_cred_nurse',
        `資格: ${t('search:params.search.cred.nurse') as string}`,
      ),
    )
  }
  if (state?.search?.doula_certification) {
    items.push(
      makeTrackItem(
        'search_doula_certification',
        `資格: ${t('search:params.search.doulaCertification') as string}`,
      ),
    )
  }
  if (state?.search?.doula_certification_official) {
    items.push(
      makeTrackItem(
        'search_doula_certification_official',
        `資格: ${
          t('search:params.search.doulaCertificationOfficial') as string
        }`,
      ),
    )
  }
  if (state?.search?.nursing_doula) {
    items.push(
      makeTrackItem(
        'search_nursing_doula',
        `資格: ${t('search:params.search.nursingDoula') as string}`,
      ),
    )
  }
  if (state?.search?.doulaedu_certification) {
    items.push(
      makeTrackItem(
        'search_doulaedu_certification',
        `資格: ${t('search:params.search.doulaeduCertification') as string}`,
      ),
    )
  }

  // 家庭教師
  if (state?.search?.tutor_tutor_supplementary) {
    items.push(
      makeTrackItem(
        'search_tutor_tutor_supplementary',
        `家庭教師: ${
          t('search:params.search.tutorTutorSupplementary') as string
        }`,
      ),
    )
  }
  if (state?.search?.tutor_english_bilingual) {
    items.push(
      makeTrackItem(
        'search_tutor_english_bilingual',
        `家庭教師: ${
          t('search:params.search.tutorEnglishBilingual') as string
        }`,
      ),
    )
  }
  if (state?.search?.tutor_entrance_exam) {
    items.push(
      makeTrackItem(
        'search_tutor_entrance_exam',
        `家庭教師: ${t('search:params.search.tutorEntranceExam') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_tokyo_university) {
    items.push(
      makeTrackItem(
        'search_tutor_tokyo_university',
        `家庭教師: ${t('search:params.search.tutorTokyoUniversity') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_keio_university) {
    items.push(
      makeTrackItem(
        'search_tutor_keio_university',
        `家庭教師: ${t('search:params.search.tutorKeioUniversity') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_waseda_university) {
    items.push(
      makeTrackItem(
        'search_tutor_waseda_university',
        `家庭教師: ${
          t('search:params.search.tutorWasedaUniversity') as string
        }`,
      ),
    )
  }
  if (state?.search?.tutor_sophia_university) {
    items.push(
      makeTrackItem(
        'search_tutor_sophia_university',
        `家庭教師: ${
          t('search:params.search.tutorSophiaUniversity') as string
        }`,
      ),
    )
  }
  if (state?.search?.tutor_other_university) {
    items.push(
      makeTrackItem(
        'search_tutor_other_university',
        `家庭教師: ${t('search:params.search.tutorOtherUniversity') as string}`,
      ),
    )
  }

  // 受験
  if (state?.search?.tutor_elementary_exam) {
    items.push(
      makeTrackItem(
        'search_tutor_elementary_exam',
        `受験: ${t('search:params.search.tutorElementaryExam') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_jrhighschool_exam) {
    items.push(
      makeTrackItem(
        'search_tutor_jrhighschool_exam',
        `受験: ${t('search:params.search.tutorJrhighschoolExam') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_highschool_exam) {
    items.push(
      makeTrackItem(
        'search_tutor_highschool_exam',
        `受験: ${t('search:params.search.tutorHighschoolExam') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_college_exam) {
    items.push(
      makeTrackItem(
        'search_tutor_college_exam',
        `受験: ${t('search:params.search.tutorCollegeExam') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_elementary_exam_ex) {
    items.push(
      makeTrackItem(
        'search_tutor_elementary_exam_ex',
        `受験: ${t('search:params.search.tutorElementaryExamEx') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_jrhighschool_exam_ex) {
    items.push(
      makeTrackItem(
        'search_tutor_jrhighschool_exam_ex',
        `受験: ${t('search:params.search.tutorJrhighschoolExamEx') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_highschool_exam_ex) {
    items.push(
      makeTrackItem(
        'search_tutor_highschool_exam_ex',
        `受験: ${t('search:params.search.tutorHighschoolExamEx') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_college_exam_ex) {
    items.push(
      makeTrackItem(
        'search_tutor_college_exam_ex',
        `受験: ${t('search:params.search.tutorCollegeExamEx') as string}`,
      ),
    )
  }

  // 科目
  if (state?.search?.tutor_national_language) {
    items.push(
      makeTrackItem(
        'search_tutor_national_language',
        `科目: ${t('search:params.search.tutorNationalLanguage') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_arithmetic) {
    items.push(
      makeTrackItem(
        'search_tutor_arithmetic',
        `科目: ${t('search:params.search.tutorArithmetic') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_science) {
    items.push(
      makeTrackItem(
        'search_tutor_science',
        `科目: ${t('search:params.search.tutorScience') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_social_studies) {
    items.push(
      makeTrackItem(
        'search_tutor_social_studies',
        `科目: ${t('search:params.search.tutorSocialStudies') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_english) {
    items.push(
      makeTrackItem(
        'search_tutor_english',
        `科目: ${t('search:params.search.tutorEnglish') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_english_speaking) {
    items.push(
      makeTrackItem(
        'search_tutor_english_speaking',
        `科目: ${t('search:params.search.tutorEnglishSpeaking') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_toeic) {
    items.push(
      makeTrackItem(
        'search_tutor_toeic',
        `科目: ${t('search:params.search.tutorToeic') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_toefl) {
    items.push(
      makeTrackItem(
        'search_tutor_toefl',
        `科目: ${t('search:params.search.tutorToefl') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_english_exam) {
    items.push(
      makeTrackItem(
        'search_tutor_english_exam',
        `科目: ${t('search:params.search.tutorEnglishExam') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_math) {
    items.push(
      makeTrackItem(
        'search_tutor_math',
        `科目: ${t('search:params.search.tutorMath') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_ancient_writing) {
    items.push(
      makeTrackItem(
        'search_tutor_ancient_writing',
        `科目: ${t('search:params.search.tutorAncientWriting') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_chinese_classics) {
    items.push(
      makeTrackItem(
        'search_tutor_chinese_classics',
        `科目: ${t('search:params.search.tutorChineseClassics') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_essay) {
    items.push(
      makeTrackItem(
        'search_tutor_essay',
        `科目: ${t('search:params.search.tutorEssay') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_physics) {
    items.push(
      makeTrackItem(
        'search_tutor_physics',
        `科目: ${t('search:params.search.tutorPhysics') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_chemistry) {
    items.push(
      makeTrackItem(
        'search_tutor_chemistry',
        `科目: ${t('search:params.search.tutorChemistry') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_biology) {
    items.push(
      makeTrackItem(
        'search_tutor_biology',
        `科目: ${t('search:params.search.tutorBiology') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_geoscience) {
    items.push(
      makeTrackItem(
        'search_tutor_geoscience',
        `科目: ${t('search:params.search.tutorGeoscience') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_japanese_history) {
    items.push(
      makeTrackItem(
        'search_tutor_japanese_history',
        `科目: ${t('search:params.search.tutorJapaneseHistory') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_world_history) {
    items.push(
      makeTrackItem(
        'search_tutor_world_history',
        `科目: ${t('search:params.search.tutorWorldHistory') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_geography) {
    items.push(
      makeTrackItem(
        'search_tutor_geography',
        `科目: ${t('search:params.search.tutorGeography') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_ethics) {
    items.push(
      makeTrackItem(
        'search_tutor_ethics',
        `科目: ${t('search:params.search.tutorEthics') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_politics_economics) {
    items.push(
      makeTrackItem(
        'search_tutor_politics_economics',
        `科目: ${t('search:params.search.tutorPoliticsEconomics') as string}`,
      ),
    )
  }

  // サポート内容
  if (state?.search?.pickup_only_support) {
    items.push(
      makeTrackItem(
        'search_pickup_only_support',
        `サポート内容: ${
          t('search:params.search.pickupOnlySupport') as string
        }`,
      ),
    )
  }
  if (state?.search?.mid_night_support) {
    items.push(
      makeTrackItem(
        'search_mid_night_support',
        `サポート内容: ${t('search:params.search.midNightSupport') as string}`,
      ),
    )
  }
  if (state?.search?.handicap_support) {
    items.push(
      makeTrackItem(
        'search_handicap_support',
        `サポート内容: ${t('search:params.search.handicapSupport') as string}`,
      ),
    )
  }
  if (state?.search?.tutor_enable_flag) {
    items.push(
      makeTrackItem(
        'search_tutor_enable_flag',
        `サポート内容: ${t('search:params.search.tutorEnableFlag') as string}`,
      ),
    )
  }
  if (state?.search?.english_lesson_support) {
    items.push(
      makeTrackItem(
        'search_english_lesson_support',
        `サポート内容: ${
          t('search:params.search.englishLessonSupport') as string
        }`,
      ),
    )
  }
  if (state?.search?.music_lesson_support) {
    items.push(
      makeTrackItem(
        'search_musicLesson_support',
        `サポート内容: ${
          t('search:params.search.musicLessonSupport') as string
        }`,
      ),
    )
  }
  if (state?.search?.sports_lesson_support) {
    items.push(
      makeTrackItem(
        'search_sports_lesson_support',
        `サポート内容: ${
          t('search:params.search.sportsLessonSupport') as string
        }`,
      ),
    )
  }
  if (state?.search?.art_lesson_support) {
    items.push(
      makeTrackItem(
        'search_art_lesson_support',
        `サポート内容: ${t('search:params.search.artLessonSupport') as string}`,
      ),
    )
  }
  if (state?.search?.other_appeal_support) {
    items.push(
      makeTrackItem(
        'search_other_appeal_support',
        `サポート内容: ${
          t('search:params.search.otherAppealSupport') as string
        }`,
      ),
    )
  }
  if (state?.search?.housekeeping_clean_up_support) {
    items.push(
      makeTrackItem(
        'search_housekeeping_clean_up_support',
        `サポート内容: ${
          t('search:params.search.housekeepingCleanUpSupport') as string
        }`,
      ),
    )
  }
  if (state?.search?.housekeeping_tidy_up_support) {
    items.push(
      makeTrackItem(
        'search_housekeeping_tidy_up_support',
        `サポート内容: ${
          t('search:params.search.housekeepingTidyUpSupport') as string
        }`,
      ),
    )
  }
  if (state?.search?.housekeeping_home_cooking_support) {
    items.push(
      makeTrackItem(
        'search_housekeeping_home_cooking_support',
        `サポート内容: ${
          t('search:params.search.housekeepingHomeCookingSupport') as string
        }`,
      ),
    )
  }
  if (state?.search?.housekeeping_shopping) {
    items.push(
      makeTrackItem(
        'search_housekeeping_shopping',
        `サポート内容: ${
          t('search:params.search.housekeepingShopping') as string
        }`,
      ),
    )
  }
  if (state?.search?.housekeeping_cooking_in_advance_support) {
    items.push(
      makeTrackItem(
        'search_housekeeping_cooking_in_advance_support',
        `サポート内容: ${
          t(
            'search:params.search.housekeepingCookingInAdvanceSupport',
          ) as string
        }`,
      ),
    )
  }
  if (state?.search?.housekeeping_home_party_cooking) {
    items.push(
      makeTrackItem(
        'search_housekeeping_home_party_cooking',
        `サポート内容: ${
          t('search:params.search.housekeepingHomePartyCooking') as string
        }`,
      ),
    )
  }

  // 対応時間帯
  if (state?.search?.morning_support) {
    items.push(
      makeTrackItem(
        'search_morning_support',
        `対応時間帯: ${t('search:params.search.morningSupport') as string}`,
      ),
    )
  }
  if (state?.search?.night_support) {
    items.push(
      makeTrackItem(
        'search_night_support',
        `対応時間帯: ${t('search:params.search.nightSupport') as string}`,
      ),
    )
  }

  // 病児
  if (state?.search?.sick_bsl_fever) {
    items.push(
      makeTrackItem(
        'search_sick_bsl_fever',
        `病児: ${t('search:params.search.sickBslFever') as string}`,
      ),
    )
  }
  if (state?.search?.sick_bsl_unknown_disease) {
    items.push(
      makeTrackItem(
        'search_sick_bsl_unknown_disease',
        `病児: ${t('search:params.search.sickBslUnknownDisease') as string}`,
      ),
    )
  }
  if (state?.search?.sick_bsl_infection) {
    items.push(
      makeTrackItem(
        'search_sick_bsl_infection',
        `病児: ${t('search:params.search.sickBslInfection') as string}`,
      ),
    )
  }
  if (state?.search?.sick_bsl_unknown) {
    items.push(
      makeTrackItem(
        'search_sick_bsl_unknown',
        `病児: ${t('search:params.search.sickBslUnknown') as string}`,
      ),
    )
  }

  // 病後児
  if (state?.search?.illness_bsl_fever) {
    items.push(
      makeTrackItem(
        'search_illness_bsl_fever',
        `病後児: ${t('search:params.search.illnessBslFever') as string}`,
      ),
    )
  }
  if (state?.search?.illness_bsl_unknown_disease) {
    items.push(
      makeTrackItem(
        'search_illness_bsl_unknown_disease',
        `病後児: ${
          t('search:params.search.illnessBslUnknownDisease') as string
        }`,
      ),
    )
  }
  if (state?.search?.illness_bsl_infection) {
    items.push(
      makeTrackItem(
        'search_illness_bsl_infection',
        `病後児: ${t('search:params.search.illnessBslInfection') as string}`,
      ),
    )
  }

  // 特徴
  if (state?.search?.no_option) {
    items.push(
      makeTrackItem(
        'search_no_option',
        `特徴: ${t('search:params.search.noOption') as string}`,
      ),
    )
  }
  if (state?.search?.foreign_language_support) {
    items.push(
      makeTrackItem(
        'search_foreign_language_support',
        `特徴: ${t('search:params.search.foreignLanguageSupport') as string}`,
      ),
    )
  }
  if (state?.search?.pickup_only_support) {
    items.push(
      makeTrackItem(
        'search_pickup_only_support',
        `特徴: ${t('search:params.search.pickupOnlySupport') as string}`,
      ),
    )
  }
  if (state?.search?.handicap_support) {
    items.push(
      makeTrackItem(
        'search_handicap_support',
        `特徴: ${t('search:params.search.handicapSupport') as string}`,
      ),
    )
  }
  if (state?.search?.new_debuted_sitter) {
    items.push(
      makeTrackItem(
        'search_new_debuted_sitter',
        `特徴: ${t('search:params.search.newDebutedSitter') as string}`,
      ),
    )
  }
  if (state?.search?.new_debuted_housekeeper) {
    items.push(
      makeTrackItem(
        'search_new_debuted_housekeeper',
        `特徴: ${t('search:params.search.newDebutedHousekeeper') as string}`,
      ),
    )
  }
  if (state?.search?.housekeeping_clean_with_cooking) {
    items.push(
      makeTrackItem(
        'search_housekeeping_clean_with_cooking',
        `特徴: ${
          t('search:params.search.housekeepingCleanWithCooking') as string
        }`,
      ),
    )
  }
  if (state?.search?.housekeeping_cooking_for_food_industry) {
    items.push(
      makeTrackItem(
        'search_housekeeping_cooking_for_food_industry',
        `特徴: ${
          t('search:params.search.housekeepingCookingForFoodIndustry') as string
        }`,
      ),
    )
  }
  if (state?.search?.semi_childcare_certification) {
    items.push(
      makeTrackItem(
        'search_semi_childcare_certification',
        `特徴: ${
          t('search:params.search.semiChildcareCertification') as string
        }`,
      ),
    )
  }

  if (state?.search?.word) {
    items.push(
      makeTrackItem('search_word', `キーワードで絞る: ${state.search.word}`),
    )
  }

  return items
}

export const makeSearchScheduleTrackingData = (
  state: ISearchConditionState,
): ITrackEventItem[] => {
  const items: ITrackEventItem[] = []

  /*
   * 単発
   */

  // 日付指定
  if (state?.search?.work_date_from) {
    items.push(
      makeTrackItem(
        'search_work_date_from',
        `日付指定: ${dateToStringWithFormat(
          state.search.work_date_from,
          'yyyy年MM月dd日',
        )}`,
      ),
    )
  }
  // 時間指定(開始)
  if (state?.search?.work_time_from) {
    items.push(
      makeTrackItem(
        'search_work_time_from',
        `時間指定(開始): ${state.search.work_time_from}`,
      ),
    )
  }
  if (state?.search?.work_time_from_unselected) {
    items.push(
      makeTrackItem('search_work_time_from', `時間指定(開始): 指定無し`),
    )
  }
  // 時間指定(終了)
  if (state?.search?.work_time_to) {
    items.push(
      makeTrackItem(
        'search_work_time_to',
        `時間指定(終了): ${state.search.work_time_to}`,
      ),
    )
  }
  if (state?.search?.work_time_to_unselected) {
    items.push(makeTrackItem('search_work_time_to', `時間指定(終了): 指定無し`))
  }

  /*
   * 定期
   */

  return items
}

export const makeSearchTrackingGroup = (
  state: ISearchConditionState,
  t: TFunction,
): string => {
  return t(
    `search:params.search.searchType.${state.search.search_type}`,
  ) as string
}

export const SearchCondition = ({
  submitLabel,
  prefectures,
  showSearchCount,
  getValues,
  ...props
}: IProps): JSX.Element => {
  const { t } = useTranslation()
  const user = React.useContext(trackingUserContext)
  const eventIds = React.useContext(searchEventIdContext)

  const reserveType = [
    'top',
    'regularLpBottom',
    'leftNavi',
    'searchResultForm',
  ].includes(getValues('pageType'))
    ? getValues('reserveType')
    : 'regular'

  const search = getValues('search')
  const regularSearch = getValues('regular_search')
  const useFormObject = useForm<ISearchConditionState>({
    defaultValues: {
      reserveType,
      search: search ? search : {},
      regular_search: regularSearch ? regularSearch : {},
    },
  })
  const { register, setValue, watch } = useFormObject
  const getLocalStateValues = useFormObject.getValues
  const [resetMessage, setResetMessage] = React.useState<boolean>(false)

  // lintだとswiperの型が認識できないためチェックを無効にする
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const [swiper, setSwiper] = React.useState<Swiper>(undefined)
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const [submitSwiper, setSubmitSwiper] = React.useState<Swiper>(undefined)

  const swipeSpeed = isMobileDisplay()
    ? MOBIL_TRANSITION_SPEED
    : TRANSITION_SPEED
  const [conditionModalDisplay, setConditionModalDisplay] =
    React.useState<boolean>(true)
  const [prefectureModalDisplay, setPrefectureModalDisplay] =
    React.useState<boolean>(false)
  const [currentPrefecture, setPrefecture] =
    React.useState<IPrefecture>(undefined)
  const [dateTimeModalDisplay, setDateTimeModalDisplay] =
    React.useState<boolean>(false)
  const [dateTimeRegularModalDisplay, setDateTimeRegularModalDisplay] =
    React.useState<boolean>(false)
  const changeSearchTypeHandler = (selectedSerchType: ISearchType) => {
    initConditionStateWithSearchType(
      selectedSerchType,
      getLocalStateValues,
      setValue,
    )
  }

  /**
   * 日付時間を選択を開く
   * 日付時間を選択へ
   */
  const openDateTimeHandler = () => {
    if (selectedReserveType === 'regular') {
      setDateTimeRegularModalDisplay(true)
    } else {
      setDateTimeModalDisplay(true)
    }
    // lintだとswiperの型が認識できないためチェックを無効にする
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    swiper.slideNext(swipeSpeed)
  }

  /**
   * 日付時間を選択から絞り込み一覧へ
   */
  const closeDateTimeHandler = () => {
    setConditionModalDisplay(true)
    // lintだとswiperの型が認識できないためチェックを無効にする
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    swiper.slidePrev(swipeSpeed)
  }

  /**
   * 単発予約
   * 絞り込み一覧へ
   */
  const submitDateTimeHandler = (state: ISearchDateTimeState) => {
    const search = { ...getValues('search'), ...state.search }
    setValue('search', search)
    setConditionModalDisplay(true)
    // lintだとswiperの型が認識できないためチェックを無効にする
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    swiper.slidePrev(swipeSpeed)
  }

  /**
   * 定期予約
   * 絞り込み一覧へ
   */
  const submitDateTimeRegularHandler = (state: ISearchRegularState) => {
    const search = { ...getValues('regular_search'), ...state.regular_search }
    setValue('regular_search', search)
    setConditionModalDisplay(true)
    // lintだとswiperの型が認識できないためチェックを無効にする
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    swiper.slidePrev(swipeSpeed)
  }

  const openPrefectureHandler = () => {
    setPrefectureModalDisplay(true)
    // lintだとswiperの型が認識できないためチェックを無効にする
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    swiper.slideNext(swipeSpeed)
  }

  const closePrefectureHandler = () => {
    setConditionModalDisplay(true)
    // lintだとswiperの型が認識できないためチェックを無効にする
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    swiper.slidePrev(swipeSpeed)
  }

  const resetPrefectureHandler = () => {
    setValue('search.geocode', {})
    setValue('search.station', {})
    setResetMessage(true)
    setTimeout(() => {
      setResetMessage(false)
    }, 2000)
  }

  const openAreaSelectHandler = (
    prefectureId: number,
    prefectureNmae: string,
  ) => {
    setPrefecture({ id: prefectureId, name: prefectureNmae })
    // lintだとswiperの型が認識できないためチェックを無効にする
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    submitSwiper.slideNext(swipeSpeed)
  }

  const closeAreaSelectHandler = () => {
    setPrefectureModalDisplay(true)
    // lintだとswiperの型が認識できないためチェックを無効にする
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    submitSwiper.slidePrev(swipeSpeed)
  }

  const transitionEndHandler = (activeIndex: number) => {
    if (activeIndex === 0) {
      // 初期表示
      setPrefectureModalDisplay(false)
      setDateTimeModalDisplay(false)
      setDateTimeRegularModalDisplay(false)
    } else if (activeIndex === 1) {
      // 都道府県表示
      setConditionModalDisplay(false)
      setPrefecture(undefined)
    }
  }

  const resetHandler = () => {
    trackEvent(eventIds.SEARCH_MODAL_CONDITION_RESET, user)

    setValue('reserveType', DEFAULT_STATE_RESERVE_TYPE)
    setValue('search', DEFAULT_STATE_SEARCH)
    setValue('regular_search', DEFAULT_STATE_REGULAR_SEARCH)
  }

  const submitHandler = () => {
    const items = makeSearchConditionTrackingData(watchAllFields, t)
    trackEvent({ ...eventIds.SEARCH_MODAL_CONDITION_SUBMIT, items }, user)

    props.submitHandler(watchAllFields)
  }

  const closeHandler = () => {
    trackEvent(eventIds.SEARCH_MODAL_CONDITION_CLOSE, user)

    props.closeHandler()
  }

  const submitSearchTopHandler = () => {
    setConditionModalDisplay(true)
    setPrefectureModalDisplay(true)
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    submitSwiper.slideTo(0, swipeSpeed)
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    swiper.slidePrev(swipeSpeed)
  }

  /*------------------ 表示イベントの計測 ------------------*/
  React.useEffect(() => {
    if (conditionModalDisplay) {
      trackEvent(eventIds.SEARCH_MODAL_CONDITION_VIEW, user)
    }
  }, [conditionModalDisplay])

  const watchAllFields = watch()
  const selectedGeocodes = watchAllFields.search.geocode
  const selectedStations = watchAllFields.search.station
  const selectedSearchType = watchAllFields.search.search_type
  const selectedReserveType = watchAllFields.reserveType

  // 本番の場合、件数取得に時間がかかりすぎるため一旦、機能をOFFにする
  /*
  React.useEffect(() => {
    if (showSearchCount && !!watchAllFields) {
      if (selectedReserveType === 'normal') {
        getSearchCount(watchAllFields)
          .then((result) => {
            if (result.result) {
              setTotalCount(result.res.total)
            }
          })
          .catch(() => void 0) // エラー時の処理を行う場合は実装する
      } else {
        getRegularSearchCount(watchAllFields)
          .then((result) => {
            if (result.result) {
              setTotalCount(result.res.total)
            }
          })
          .catch(() => void 0) // エラー時の処理を行う場合は実装する
      }
    }
  }, [JSON.stringify(watchAllFields)])
  */

  return (
    <SearchConditionPresenter
      {...props}
      submitLabel={submitLabel}
      conditionModalDisplay={conditionModalDisplay}
      prefectureModalDisplay={prefectureModalDisplay}
      currentPrefecture={currentPrefecture}
      prefectures={prefectures}
      selectedGeocodes={selectedGeocodes}
      selectedStations={selectedStations}
      selectedSearchType={selectedSearchType}
      selectedReserveType={selectedReserveType}
      resetMessage={resetMessage}
      submitSearchTopHandler={submitSearchTopHandler}
      setSwiper={setSwiper}
      setSubmitSwiper={setSubmitSwiper}
      register={register}
      getValues={getLocalStateValues}
      setValue={setValue}
      changeSearchTypeHandler={changeSearchTypeHandler}
      openPrefectureHandler={openPrefectureHandler}
      closePrefectureHandler={closePrefectureHandler}
      resetPrefectureHandler={resetPrefectureHandler}
      openAreaSelectHandler={openAreaSelectHandler}
      closeAreaSelectHandler={closeAreaSelectHandler}
      transitionEndHandler={transitionEndHandler}
      resetHandler={resetHandler}
      submitHandler={submitHandler}
      closeHandler={closeHandler}
      dateTimeModalDisplay={dateTimeModalDisplay}
      dateTimeRegularModalDisplay={dateTimeRegularModalDisplay}
      openDateTimeHandler={openDateTimeHandler}
      closeDateTimeHandler={closeDateTimeHandler}
      submitDateTimeHandler={submitDateTimeHandler}
      submitDateTimeRegularHandler={submitDateTimeRegularHandler}
      searchState={watchAllFields}
    />
  )
}

export default SearchCondition
